import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { salesLeadAPI } from 'services/apisService';
import ContactDetailsModal from 'components/ContactDetailsModal';
import SearchBy from 'components/SearchBy';
import UserListDropdown from 'components/Contact/UserListDropdown';
import ClosedWonModal from 'components/ClosedWonModal';
import { useNavigate } from 'react-router-dom';

const SortableItem = ({ id, children, disabled = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    disabled 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...(disabled ? {} : listeners)}>
      {children}
    </div>
  );
};

const DroppableContainer = ({ id, children, items }) => {
  const {
    setNodeRef,
    isOver,
  } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 p-2 overflow-y-auto min-h-[400px] bg-[#bed9eb] transition-colors duration-200 ${
        isOver ? 'bg-blue-100' : ''
      }`}
    >
      {children}
    </div>
  );
};

const LeadCard = ({ item, columnId, onLeadClick, disabled = false  }) => {
  const truncateText = (text, maxLength = 60) => {
    if (!text) return 'No recent note';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const truncateName = (name, maxLength = 20) => {
    if (!name) return '---';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };
  const truncateCompany = (name, maxLength = 30) => {
    if (!name) return '---';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  return (
    <div
      onClick={() => onLeadClick(item.id, columnId)}
      className={`bg-white rounded-lg p-4 mb-3 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer ${
        disabled ? 'opacity-75 cursor-not-allowed' : ''
      }`}
     style={{
        background: disabled 
          ? 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)' 
          : 'linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)',
      }}
    >
      {/* Contact Name */}
      <div className="text-[#2c5282] font-semibold text-base mb-2 flex items-center">
        <div className="w-2 h-2 bg-[#7fb3d3] rounded-full mr-2 flex-shrink-0"></div>
        <span className="truncate flex-1 mr-2">
          {truncateName(item.Contact?.name || item.name)}
        </span>
        
          {/* Engagement Stage Badge */}
          {disabled ? (
          <div className="flex-shrink-0 flex items-center rounded-full px-2 py-0.5 bg-green-600 shadow-sm max-h-[20px]">
            <svg className="w-3 h-3 mr-1 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-[0.6rem] font-bold uppercase tracking-wide text-white">Deal Closed</span>
          </div>
        ) : (
          item.engagement_stage && (
            <div
              className={`flex-shrink-0 flex items-center rounded-full px-1.5 shadow-sm max-h-[20px] ${
                item.engagement_stage === 'hot_lead'
                  ? 'bg-gradient-to-r from-red-500 to-orange-500'
                  : item.engagement_stage === 'warm_lead'
                  ? 'bg-gradient-to-r from-yellow-100 to-yellow-200'
                  : 'bg-gradient-to-r from-purple-50 to-purple-100'
              }`}
            >
              <div
                className={`w-1.5 h-1.5 rounded-full mr-1 animate-pulse ${
                  item.engagement_stage === 'hot_lead'
                    ? 'bg-white'
                    : item.engagement_stage === 'warm_lead'
                    ? 'bg-yellow-800'
                    : 'bg-blue-800'
                }`}
              ></div>
              <span
                className={`text-[0.6rem] font-bold uppercase tracking-wide ${
                  item.engagement_stage === 'hot_lead'
                    ? 'text-white'
                    : item.engagement_stage === 'warm_lead'
                    ? 'text-yellow-800'
                    : 'text-blue-900'
                }`}
              >
                {item.engagement_stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            </div>
          )
        )}


      </div>
      
      {/* Company Name - Highlighted */}
      <div className="mb-3">
        <div className="bg-gradient-to-r from-[#e6f2ff] to-[#cce7ff] border border-[#7fb3d3] rounded-md px-2 py-1">
          <span className=" truncate text-[#2c5282] font-bold text-sm note-wrap">
             {truncateCompany(item.Contact?.companyName || item.companyName || '---')}
          </span>
        </div>
      </div>
      
      {/* Contact Information */}
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex items-center bg-[#f0f8ff] rounded-md px-2 py-1">
          <span className="text-[#3985b6] mr-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </span>
          <span className="text-sm text-gray-700 truncate">{item.Contact?.email || item.email || '---'}</span>
        </div>
        <div className="flex items-center bg-[#f0f8ff] rounded-md px-2 py-1">
          <span className="text-[#3985b6] mr-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </span>
          <span className="text-sm text-gray-700 truncate">{item.Contact?.phone || item.phone || '---'}</span>
        </div>
      </div>
      
      {/* Recent Note */}
      <div className="bg-gradient-to-r from-yellow-50 to-blue-50 border border-yellow-200 rounded-md p-2 h-[75px]">
        <div className="flex flex-col">
          <div className='flex items-center justify-center'>
            <div className="w-2 h-2 bg-[#7fb3d3] rounded-full mr-2 flex-shrink-0"></div>
            <div className="flex-1">
              <span className="text-xs text-[#3985b6] font-semibold block">Recent Note</span>
            </div>
          </div>
          <div className="text-sm text-gray-700 note-wrap leading-tight pt-1">
            {truncateText(item.Contact?.recentNote || item.recentNote)}
          </div>
        </div>
      </div>
    </div>
  );
};

const Deals = () => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [laneLeadIds, setLaneLeadIds] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedSearchField, setSelectedSearchField] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [searchDate, setSearchDate] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [showEngagementDropdown, setShowEngagementDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const engagementDropdownRef = useRef(null);
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const usersDropdownRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const [userName, setUserName] = useState(user?.role);
  const [showClosedWonModal, setShowClosedWonModal] = useState(false);
  const [pendingClosedWonLead, setPendingClosedWonLead] = useState(null);
  const [showPilotPlanModal, setShowPilotPlanModal] = useState(false);
  const [pendingPilotPlanLead, setPendingPilotPlanLead] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const statusMapping = {
    'new_lead': 'NEW LEAD',
    'engaged': 'ENGAGED',
    'demo_scheduled': 'DEMO SCHEDULED',
    'demo_completed': 'DEMO COMPLETED',
    'negotiation': 'NEGOTIATION',
    'proposal_sent': 'PROPOSAL SENT',
    'pilot_plan': 'PILOT PLAN',
    'closed_won': 'CLOSED WON',
    'closed_lost': 'CLOSED LOST',
    're_engagement': 'RE-ENGAGEMENT'
  };

const handleClosedWonSubmit = async (formData) => {
  try {
    setShowClosedWonModal(false); 
    setPendingClosedWonLead(null);
    
    setSuccessMessage(`Deal closed successfully! Amount: $${formData.amount}`);
    setTimeout(() => setSuccessMessage(''), 3000);
    
  } catch (error) {
    console.error('❌ Error:', error);
    setErrorMessage('Failed to mark lead as closed won. Please try again.');
    setTimeout(() => setErrorMessage(''), 3000);
  }
};

const handlePilotPlanSubmit = async (formData) => {
  try {
    const leadId = pendingPilotPlanLead?.leadId;
    
    if (leadId) {
      setColumns((prevColumns) => {
        const updatedColumns = { ...prevColumns };
        
        if (updatedColumns['pilot_plan'] && updatedColumns['pilot_plan'].items) {
          updatedColumns['pilot_plan'] = {
            ...updatedColumns['pilot_plan'],
            items: updatedColumns['pilot_plan'].items.map(item => 
              item.id === leadId 
                ? { ...item, boughtPilotPlan: true }
                : item
            )
          };
        }
        
        return updatedColumns;
      });
    }
    
    setShowPilotPlanModal(false); 
    setPendingPilotPlanLead(null);
    
    setSuccessMessage(`Lead moved to pilot plan successfully! Amount: ₹${formData.amount}`);
    setTimeout(() => setSuccessMessage(''), 3000);
    
  } catch (error) {
    console.error('❌ Error:', error);
    setErrorMessage('Failed to move lead to pilot plan. Please try again.');
    setTimeout(() => setErrorMessage(''), 3000);
  }
};

const handleClosedWonCancel = () => {
  
  setShowClosedWonModal(false);
  
  if (pendingClosedWonLead) {
    const { leadId, sourceContainer, leadData } = pendingClosedWonLead;
    
    setColumns((prevColumns) => {
      const newColumns = { ...prevColumns };
      
      newColumns['closed_won'] = {
        ...newColumns['closed_won'],
        items: newColumns['closed_won'].items.filter(item => item.id !== leadId),
        count: newColumns['closed_won'].items.length - 1
      };
      
      newColumns[sourceContainer] = {
        ...newColumns[sourceContainer],
        items: [...newColumns[sourceContainer].items, leadData],
        count: newColumns[sourceContainer].items.length + 1
      };
      
      return newColumns;
    });
  }
  
  setPendingClosedWonLead(null);
};

const handlePilotPlanCancel = () => {
  
  setShowPilotPlanModal(false);
  
  if (pendingPilotPlanLead) {
    const { leadId, sourceContainer, leadData } = pendingPilotPlanLead;
    
    setColumns((prevColumns) => {
      const newColumns = { ...prevColumns };
      
      newColumns['pilot_plan'] = {
        ...newColumns['pilot_plan'],
        items: newColumns['pilot_plan'].items.filter(item => item.id !== leadId),
        count: newColumns['pilot_plan'].items.length - 1
      };
      
      newColumns[sourceContainer] = {
        ...newColumns[sourceContainer],
        items: [...newColumns[sourceContainer].items, leadData],
        count: newColumns[sourceContainer].items.length + 1
      };
      
      return newColumns;
    });
  }
  
  setPendingPilotPlanLead(null);
};

const navigate = useNavigate();

const handleLeadUpdated = useCallback((leadId, updatedContact) => {
  setColumns(prevColumns => {
    const updatedColumns = { ...prevColumns };
    
    Object.keys(updatedColumns).forEach(columnId => {
      const column = updatedColumns[columnId];
      if (!column || !column.items) return;
      
      const leadIndex = column.items.findIndex(item => item?.id === leadId);
      if (leadIndex !== -1) {
        updatedColumns[columnId] = {
          ...column,
          items: [...column.items]
        };
        
        updatedColumns[columnId].items[leadIndex] = {
          ...updatedColumns[columnId].items[leadIndex],
          name: updatedContact.name || updatedColumns[columnId].items[leadIndex].name,
          companyName: updatedContact.companyName || updatedColumns[columnId].items[leadIndex].companyName,
          email: updatedContact.email || updatedColumns[columnId].items[leadIndex].email,
          phone: updatedContact.phone || updatedColumns[columnId].items[leadIndex].phone,
        };
      }
    });
    
    return updatedColumns;
  });
}, []);

const handleEngagementUpdated = useCallback((leadId, newEngagementStage) => {
  setColumns(prevColumns => {
    const updatedColumns = { ...prevColumns };
    
    Object.keys(updatedColumns).forEach(columnId => {
      const column = updatedColumns[columnId];
      if (!column || !column.items) return;
      
      const leadIndex = column.items.findIndex(item => item?.id === leadId);
      if (leadIndex !== -1) {
        updatedColumns[columnId] = {
          ...column,
          items: [...column.items]
        };
        
        updatedColumns[columnId].items[leadIndex] = {
          ...updatedColumns[columnId].items[leadIndex],
          engagement_stage: newEngagementStage
        };
      }
    });
    
    return updatedColumns;
  });
}, []);
  
  const statusOrder = [
    'new_lead',
    'engaged',
    'demo_scheduled',
    'demo_completed',
    'negotiation',
    'proposal_sent',
    'pilot_plan',
    'closed_won',
    'closed_lost',
    're_engagement'
  ];

  const searchOptions = [
  { value: 'created_at', label: 'Created At', isDate: true },
  { value: 'nextFollowupCall', label: 'Next Followup Call', isDate: true },
  { value: 'nextFollowupMail', label: 'Next Followup Mail', isDate: true },
  { value: 'name', label: 'Name', isDate: false },
  { value: 'location', label: 'Location', isDate: false },
  { value: 'companyName', label: 'Company Name', isDate: false },
  { value: 'title', label: 'Title', isDate: false },
  { value: 'status', label: 'Status', isDate: false, isStatus: true },
  { value: 'engagement_stage', label: 'Engagement Stage', isDate: false, isEngagement: true }
];

const statusOptions = [
  { value: 'new_lead', label: 'NEW LEAD' },
  { value: 'engaged', label: 'ENGAGED' },
  { value: 'demo_scheduled', label: 'DEMO SCHEDULED' },
  { value: 'demo_completed', label: 'DEMO COMPLETED' },
  { value: 'negotiation', label: 'NEGOTIATION' },
  { value: 'proposal_sent', label: 'PROPOSAL SENT' },
  { value: 'pilot_plan', label: 'PILOT PLAN' },
  { value: 'closed_won', label: 'CLOSED WON' },
  { value: 'closed_lost', label: 'CLOSED LOST' },
  { value: 're_engagement', label: 'RE-ENGAGEMENT' }
];

const engagementOptions = [
  { value: 'cold_lead', label: 'COLD' },
  { value: 'warm_lead', label: 'WARM' },
  { value: 'hot_lead', label: 'HOT' }
];

const handleUserSelect = (user) => {
  setSelectedUserId(user.id);
  setShowUsersDropdown(false);
  const filtersWithUser = { ...activeFilters, userId: user.id };
  fetchSalesLeads(1, filtersWithUser);
};

const clearUserFilter = () => {
  setSelectedUserId('');
  setShowUsersDropdown(false);
  setActiveFilters({});
  setSelectedSearchField('');
  setSearchValue('');
  setSearchDate('');
  fetchSalesLeads(1, { userId: null });
};

useEffect(() => {
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
    
    if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
      setShowStatusDropdown(false);
    }
    
    if (engagementDropdownRef.current && !engagementDropdownRef.current.contains(event.target)) {
      setShowEngagementDropdown(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };
}, []);

useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const filterParam = urlParams.get('filter');
  
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  let initialFilters = {};
  let initialSearchField = '';
  let initialDate = '';

  if (filterParam === 'followupcalls') {
    initialFilters = { nextFollowupCall: getTodayDate() };
    initialSearchField = 'nextFollowupCall';
    initialDate = getTodayDate();
  } else if (filterParam === 'followupmails') {
    initialFilters = { nextFollowupMail: getTodayDate() };
    initialSearchField = 'nextFollowupMail';
    initialDate = getTodayDate();
  } else if (filterParam === 'demo_scheduled') {
    initialFilters = { status: 'demo_scheduled' };
    initialSearchField = 'status';
  } else if (filterParam === 'closed_won') {
    initialFilters = { status: 'closed_won' };
    initialSearchField = 'status';
  }

  setActiveFilters(initialFilters);
  setSelectedSearchField(initialSearchField);
  setSearchDate(initialDate);

  fetchSalesLeads(1, initialFilters);
}, []);

const fetchSalesLeads = async (page = 1, filters = {}) => {
  try {
    setIsLoading(true);
    
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('limit', 50);
    
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        queryParams.append(key, filters[key]);
      }
    });
    
    const response = await salesLeadAPI.getSalesLeads(page, 50, queryParams.toString());
    
    if (response && response.data) {
      const updatedColumns = initializeEmptyColumns(); 
      
      response.data.forEach(lead => {
        if (!lead) return; 
        
        const status = lead.status || 'new_lead';
        
        if (updatedColumns[status]) {
          const leadItem = {
          id: lead.id,
          name: lead.Contact?.name || 'Unknown Contact',
          companyName: lead.Contact?.companyName || '---',
          email: lead.Contact?.email || '',
          phone: lead.Contact?.phone || '',
          recentNote: lead.Contact?.recentNote?.content || '---',
          engagement_stage: lead?.engagement_stage || '---',
          leadContactId : lead?.Contact?.id || '---',
          boughtPilotPlan: lead?.Contact?.boughtPilotPlan || false
        };
          
          updatedColumns[status].items.push(leadItem);
        }
      });
      
      Object.keys(updatedColumns).forEach(colId => {
        const column = updatedColumns[colId];
        if (column && column.items) {
          column.count = column.items.length;
        }
      });
      
      setColumns(updatedColumns); 
    }
  } catch (err) {
    console.error("Error fetching sales leads:", err);
    setError(err.message || "Failed to load sales leads");
  } finally {
    setIsLoading(false);
  }
};

const getSelectedOption = () => {
  return searchOptions.find(option => option.value === selectedSearchField);
};

const handleSearchFieldSelect = (option) => {
  setSelectedSearchField(option.value);
  setShowDropdown(false);
  setSearchValue('');
  setSearchDate('');
  
  if (option.value === 'status') {
    setShowStatusDropdown(true);
    setShowEngagementDropdown(false);
  } 
  else if (option.value === 'engagement_stage') {
    setShowEngagementDropdown(true);
    setShowStatusDropdown(false);
  } 
  else {
    setShowStatusDropdown(false);
    setShowEngagementDropdown(false);
  }
};

const handleStatusSelect = (status) => {
  const newFilters = {
    ...activeFilters,
    status: status.value
  };
  setActiveFilters(newFilters);
  setShowStatusDropdown(false);
  fetchSalesLeads(1, newFilters);
};

const handleEngagementSelect = (engagement) => {
  const newFilters = {
    ...activeFilters,
    engagement_stage: engagement.value
  };
  setActiveFilters(newFilters);
  setShowEngagementDropdown(false);
  fetchSalesLeads(1, newFilters);
};

const handleSearch = () => {
  if (searchValue) {
    const newFilters = {
      ...activeFilters,
      [selectedSearchField]: searchValue
    };
    setActiveFilters(newFilters);
    fetchSalesLeads(1, newFilters);
  }
};

const clearFilters = () => {
  setActiveFilters({});
  setSelectedSearchField('');
  setSearchValue('');
  setSearchDate('');
  setShowDropdown(false);
  setShowStatusDropdown(false);
  setShowEngagementDropdown(false);
  navigate('/leads', { replace: true });
  fetchSalesLeads(1, {});
};
  
  const initializeEmptyColumns = () => {
    const initialColumns = {};
    
    statusOrder.forEach(status => {
      initialColumns[status] = {
        id: status,
        title: statusMapping[status],
        items: [],
        count: 0
      };
    });
    
    return initialColumns;
  };

  const [columns, setColumns] = useState(initializeEmptyColumns());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

useEffect(() => {
  if (activeFilters.status && !isLoading) {
    const targetElement = document.getElementById(`lane-${activeFilters.status}`);
    if (targetElement) {
      setTimeout(() => {
        let container = targetElement.parentElement;
        while (container && container !== document.body) {
          const style = window.getComputedStyle(container);
          if (style.overflowX === 'auto' || style.overflowX === 'scroll' || 
              style.overflow === 'auto' || style.overflow === 'scroll') {
            break;
          }
          container = container.parentElement;
        }
        
        if (container && container !== document.body) {
          const containerRect = container.getBoundingClientRect();
          const targetRect = targetElement.getBoundingClientRect();
          
          const targetCenter = targetRect.left + (targetRect.width / 2);
          const containerCenter = containerRect.left + (containerRect.width / 2);
          const scrollOffset = targetCenter - containerCenter;
          
          container.scrollTo({
            left: container.scrollLeft + scrollOffset,
            behavior: 'smooth'
          });
        }
      }, 500);
    }
  }
}, [activeFilters.status, isLoading]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleLeadClick = async (leadId, status) => {
    try {
      const leadDetails = await salesLeadAPI.getSalesLead(leadId);
      setSelectedLead(leadDetails);
      setShowModal(true);
      const currentLaneLeads = columns[status]?.items?.map(item => item.id) || [];
      setLaneLeadIds(currentLaneLeads);
    } catch (error) {
      console.error('Error fetching lead details:', error);
    }
  };

  const handleModalNavigation = useCallback(async (direction) => {
  if (!selectedLead || !laneLeadIds.length) return;
  
  const currentIndex = laneLeadIds.findIndex(id => id === selectedLead.id);
  if (currentIndex === -1) return;
  
  let newIndex;

  if (direction === 'next') {
    newIndex = (currentIndex + 1) % laneLeadIds.length;
  } else {
    newIndex = (currentIndex - 1 + laneLeadIds.length) % laneLeadIds.length;
  }

  const nextLeadId = laneLeadIds[newIndex];

  try {
    const nextLeadData = await salesLeadAPI.getSalesLead(nextLeadId);
    setSelectedLead(nextLeadData);
  } catch (error) {
    console.error('Error fetching lead details:', error);
    setSelectedLead({ id: nextLeadId });
  }
}, [selectedLead, laneLeadIds]);

const handleNoteAdded = useCallback((leadId, newNote) => {
  setColumns(prevColumns => {
    const updatedColumns = { ...prevColumns };
    
    Object.keys(updatedColumns).forEach(columnId => {
      const column = updatedColumns[columnId];
      if (!column || !column.items) return;
      
      const leadIndex = column.items.findIndex(item => item?.id === leadId);
      if (leadIndex !== -1) {
        updatedColumns[columnId] = {
          ...column,
          items: [...column.items]
        };
        updatedColumns[columnId].items[leadIndex] = {
          ...updatedColumns[columnId].items[leadIndex],
          recentNote: newNote?.content || ''
        };
      }
    });
    
    return updatedColumns;
  });
}, []);

const handleNoteUpdated = useCallback((leadId, updatedNote) => {
  setColumns(prevColumns => {
    const updatedColumns = { ...prevColumns };
    
    Object.keys(updatedColumns).forEach(columnId => {
      const column = updatedColumns[columnId];
      if (!column || !column.items) return;
      
      const leadIndex = column.items.findIndex(item => item?.id === leadId);
      if (leadIndex !== -1) {
        updatedColumns[columnId] = {
          ...column,
          items: [...column.items]
        };
        updatedColumns[columnId].items[leadIndex] = {
          ...updatedColumns[columnId].items[leadIndex],
          recentNote: updatedNote?.content || ''
        };
      }
    });
    
    return updatedColumns;
  });
}, []);

  const findContainer = (id) => {
    if (id in columns) {
      return id;
    }

    for (const columnId of statusOrder) {
      const column = columns[columnId];
      if (column && column.items && column.items.find(item => item.id === id)) {
        return columnId;
      }
    }
    return null;
  };

  const getItemIds = (containerId) => {
    const column = columns[containerId];
    return column ? column.items.map(item => item.id) : [];
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    const containerId = findContainer(active.id);
    if (containerId) {
      const column = columns[containerId];
      const item = column.items.find(item => item.id === active.id);
      setDraggedItem(item);
    }
  };

  const handleDragOver = (event) => {
  const { active, over } = event;
  
  if (!over) return;

  const activeId = active.id;
  const overId = over.id;

  const activeContainer = findContainer(activeId);
  const overContainer = findContainer(overId) || overId;

  if (!activeContainer || !overContainer) return;

  if (activeContainer !== overContainer) return;

  setColumns((prevColumns) => {
    const activeItems = prevColumns[activeContainer].items;
    const activeIndex = activeItems.findIndex(item => item.id === activeId);
    const overIndex = activeItems.findIndex(item => item.id === overId);

    if (activeIndex !== overIndex && activeIndex !== -1 && overIndex !== -1) {
      const newColumns = { ...prevColumns };
      newColumns[activeContainer] = {
        ...newColumns[activeContainer],
        items: arrayMove(activeItems, activeIndex, overIndex)
      };
      return newColumns;
    }

    return prevColumns;
  });
};

 const handleDragEnd = async (event) => {
  const { active, over } = event;
  
  if (!over) {
    setActiveId(null);
    setDraggedItem(null);
    return;
  }

  const activeId = active.id;
  const overId = over.id;

  const sourceContainer = findContainer(activeId);
  
  let targetContainer = findContainer(overId) || overId;

  if (!sourceContainer || !targetContainer) {
    setActiveId(null);
    setDraggedItem(null);
    return;
  }


  if (sourceContainer !== targetContainer) {
    const sourceItems = columns[sourceContainer].items;
    const activeIndex = sourceItems.findIndex(item => item.id === activeId);
    const leadData = sourceItems[activeIndex];
    
    const shouldSkipPilotPlanModal = targetContainer === 'pilot_plan' && leadData?.boughtPilotPlan === true;
    
    if (targetContainer === 'pilot_plan' && !shouldSkipPilotPlanModal) {
      setPendingPilotPlanLead({
        leadId: activeId,
        sourceContainer,
        leadData
      });
      setShowPilotPlanModal(true);
    }
    
    else if (targetContainer === 'closed_won') {
      setPendingClosedWonLead({
        leadId: activeId,
        sourceContainer,
        leadData
      });
      setShowClosedWonModal(true);
    }
    
    setColumns((prevColumns) => {
      const sourceItems = prevColumns[sourceContainer].items;
      const targetItems = prevColumns[targetContainer].items;
      const activeIndex = sourceItems.findIndex(item => item.id === activeId);
      
      if (activeIndex === -1) return prevColumns;
      
      const activeItem = sourceItems[activeIndex];
      const newColumns = { ...prevColumns };
      
      let insertIndex = targetItems.length; 
      
      if (overId !== targetContainer) {
        const overIndex = targetItems.findIndex(item => item.id === overId);
        if (overIndex !== -1) {
          insertIndex = overIndex;
        }
      }
      
      newColumns[sourceContainer] = {
        ...newColumns[sourceContainer],
        items: sourceItems.filter(item => item.id !== activeId),
        count: sourceItems.length - 1
      };
      
      const newTargetItems = [...targetItems];
      newTargetItems.splice(insertIndex, 0, activeItem);
      
      newColumns[targetContainer] = {
        ...newColumns[targetContainer],
        items: newTargetItems,
        count: newTargetItems.length
      };
      
      return newColumns;
    });

    
   
    if (targetContainer !== 'closed_won' && (targetContainer !== 'pilot_plan' || shouldSkipPilotPlanModal)) {
      try {
        await salesLeadAPI.updateSalesLeadStatus(activeId, targetContainer);
      } catch (error) {
        console.error('❌ API call failed:', error);
        alert('Failed to update lead status. Please try again.');
        window.location.reload();
      }
    }
    
  }

  setActiveId(null);
  setDraggedItem(null);
};

  const getStatusLabel = (value) => {
  return statusOptions.find(opt => opt.value === value)?.label || value;
};

const getEngagementLabel = (value) => {
  return engagementOptions.find(opt => opt.value === value)?.label || value;
};

const removeFilter = (filterKey) => {
  const newFilters = { ...activeFilters };
  delete newFilters[filterKey];
  setActiveFilters(newFilters);
  fetchSalesLeads(1, newFilters);
};

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
    <div className="pb-4 min-h-screen">

      <div className={`flex justify-between items-center ${userName === "ADMIN" ? 'mb-4' : 'mb-4'}`}>
      <SearchBy
        dropdownRef={dropdownRef}
        setShowDropdown={setShowDropdown}
        showDropdown={showDropdown}
        selectedSearchField={selectedSearchField}
        getSelectedOption={getSelectedOption}
        searchOptions={searchOptions}
        handleSearchFieldSelect={handleSearchFieldSelect}
        statusDropdownRef={statusDropdownRef}
        setShowStatusDropdown={setShowStatusDropdown}
        showStatusDropdown={showStatusDropdown}
        statusOptions={statusOptions}
        handleStatusSelect={handleStatusSelect}
        searchDate={searchDate}
        setSearchDate={setSearchDate}
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        searchValue={searchValue}
        fetchContacts={fetchSalesLeads}
        setSearchValue={setSearchValue}
        handleSearch={handleSearch}
        setSelectedSearchField={setSelectedSearchField}
        clearFilters={clearFilters}
        engagementDropdownRef={engagementDropdownRef}
        setShowEngagementDropdown={setShowEngagementDropdown}
        showEngagementDropdown={showEngagementDropdown}
        engagementOptions={engagementOptions}
        handleEngagementSelect={handleEngagementSelect}
      />
    <div>
      <UserListDropdown 
      usersDropdownRef={usersDropdownRef}
      setShowUsersDropdown={setShowUsersDropdown}
      showUsersDropdown={showUsersDropdown}
      selectedUserId={selectedUserId}
      handleUserSelect={handleUserSelect}
      clearUserFilter={clearUserFilter}
      apiType="sales"
    />
    </div>

    </div>
     {Object.keys(activeFilters).length > 0 && (
      <div className="flex flex-wrap gap-2 pb-4">
        {Object.entries(activeFilters).map(([key, value]) => (
          <div key={key} className="flex items-center bg-yellow-100 text-gray-800 px-4 py-[2px] rounded-full text-sm font-medium shadow-sm">
            <span className="mr-2">
              {searchOptions.find(opt => opt.value === key)?.label}: 
              <span className="font-semibold ml-1">
                {key === 'status' ? getStatusLabel(value) : 
                key === 'engagement_stage' ? getEngagementLabel(value) : value}
              </span>
            </span>
            <button
              onClick={() => removeFilter(key)}
              className="ml-2 hover:text-red-600 font-bold text-lg transition-colors duration-150"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    )}


      <div className="flex gap-2 overflow-x-auto pb-4">
        {statusOrder.map(columnId => {
          const column = columns[columnId];
          if (!column) return null;
          
          const itemIds = getItemIds(columnId);
          
          return (
            <div 
            id={`lane-${columnId}`}  
              key={columnId} 
              className="flex-1 min-w-[280px] bg-white border rounded-md flex flex-col"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="font-semibold text-sm text-gray-700">{column.title}</div>
                <div className="bg-[#3985b6] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                  {column.count || 0}
                </div>
              </div>
              
                <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
              <DroppableContainer id={columnId} items={itemIds}>
                {(column.items || []).map((item) => {
                  if (!item) return null;
                  
                  return (
                        <SortableItem key={item.id} id={item.id} disabled={columnId === 'closed_won'}>
                        <LeadCard 
                          item={item} 
                          columnId={columnId} 
                          onLeadClick={handleLeadClick}
                          disabled={columnId === 'closed_won'}
                        />
                      </SortableItem>
                      );
                    })}
                    {(!column.items || column.items.length === 0) && (
                      <div className="flex items-center justify-center h-24 text-gray-400 italic">
                        No leads in this stage
                      </div>
                    )}
                  </DroppableContainer>
                </SortableContext>
              </div>
            );
          })}
        </div>

        <DragOverlay>
          {activeId && draggedItem ? (
            <div className="transform rotate-5 opacity-90">
              <LeadCard 
                item={draggedItem} 
                columnId={findContainer(activeId)} 
                onLeadClick={() => {}}
              />
            </div>
          ) : null}
        </DragOverlay>
      </div>

      <ContactDetailsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contact={selectedLead}
        contacts={laneLeadIds}
        isLead={true}
        onNavigate={handleModalNavigation}
        onNoteAdded={handleNoteAdded}
        onNoteUpdated={handleNoteUpdated}
        onLeadUpdated={handleLeadUpdated}
        onEngagementUpdated={handleEngagementUpdated} 
      />

      <ClosedWonModal
        isOpen={showClosedWonModal}
        onClose={handleClosedWonCancel} 
        onSubmit={handleClosedWonSubmit}
        leadData={pendingClosedWonLead?.leadData}
      />

      <ClosedWonModal
        isOpen={showPilotPlanModal}
        onClose={handlePilotPlanCancel} 
        onSubmit={handlePilotPlanSubmit}
        leadData={pendingPilotPlanLead?.leadData}
        mode="pilot_plan"
        defaultAmount="1499"
      />

      {/* Success Message */}
      {successMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-100 text-green-800 px-6 py-3 rounded-md shadow-md text-center z-50 max-w-[90%] sm:max-w-md">
          {"✅  " + successMessage}
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-100 text-red-800 px-6 py-3 rounded-md shadow-md text-center z-50 max-w-[90%] sm:max-w-md">
          {"❌  " + errorMessage}
        </div>
      )}
    </DndContext>
  );
};

export default Deals;